import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Check, Copy, ExternalLink, Eye, FilePenLine, Hourglass, Link2, Link2Off, PenLine, Video } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { copiarAlPortapapeles } from '../lib/portapapeles';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { AtrVideoPlayer } from './AtrVideoPlayer';
import { fmtBytes, fmtDuration, type AtrVideoDTO } from '../lib/atrVideos';

/**
 * Cuadro "ATR" en el detalle del claim (Claims y Claim Review): TODOS los videos ATR del claim —
 * las citas, los links agregados en Files y los ARCHIVOS subidos desde el teléfono del inspector.
 * Recogidos: cada uno se despliega con el video (reproductor nativo para los archivos, embed para
 * YouTube) y, si tiene fila en Files, un editor de notas (con quién + fecha).
 *
 * ARCHIVOS SUBIDOS: la oficina genera acá el LINK PÚBLICO con el nombre que quiera (pedido del
 * dueño 2026-09-17). Ese link abre sin login con la marca y es el que viaja en el email al
 * adjuster; se puede copiar, renombrar y revocar, y se ve cuántas veces lo abrieron.
 *
 * En Review ATR (selectable): "Accept" marca CUÁL video se va a usar — un archivo subido solo se
 * acepta con su link (el interno no le abre al adjuster): el botón es "Generate link & accept".
 */
type Atr = {
  id: string; fileId?: string | null; kind: 'appt' | 'file' | 'video'; videoUrl: string | null; at: string | null;
  inspectorName: string | null; resultNotes: string | null; notes?: string | null;
  notesBy?: string | null; notesAt?: string | null; label?: string | null; status: 'uploaded' | 'pending';
  video?: AtrVideoDTO | null;
};
export type AtrAccepted = { videoRef: string; videoUrl: string | null; label: string | null; byName: string | null; at: string | null };
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** El link público del archivo: generar (con nombre) · copiar · renombrar · revocar · aperturas. */
function ShareBlock({ video, suggestedTitle, onChanged, selectable, editing, setEditing }: {
  video: AtrVideoDTO; suggestedTitle: string; onChanged: () => void; selectable?: boolean; editing: boolean; setEditing: (v: boolean) => void;
}) {
  const { t } = useI18n();
  const share = video.share;
  const [title, setTitle] = useState(share?.title ?? video.title ?? suggestedTitle);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (editing) setTitle(share?.title ?? video.title ?? suggestedTitle); }, [editing, share?.title, video.title, suggestedTitle]);

  async function guardar() {
    setBusy(true); setErr(null);
    try { await api.post(`/api/atr-videos/${video.id}/share`, { title: title.trim() || null }); setEditing(false); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }
  async function revocar() {
    if (!window.confirm(t('atr.revokeConfirm'))) return;
    setBusy(true); setErr(null);
    try { await api.post(`/api/atr-videos/${video.id}/share/revoke`, {}); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }
  async function copy() {
    if (!share) return;
    // El fallback de seleccionar el input vivía acá; ahora copia por el helper, igual que el resto.
    const ok = await copiarAlPortapapeles(share.url);
    setCopied(ok); if (ok) setTimeout(() => setCopied(false), 2000);
  }

  const cabecera = (
    <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
      <Link2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.link')}
    </span>
  );

  if (editing || !share) {
    return (
      <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
        {cabecera}
        {editing ? (
          <>
            <label className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('atr.linkTitle')}</label>
            <input className="input" style={{ fontSize: 'var(--fs-xs)' }} value={title} maxLength={140} autoFocus onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void guardar(); if (e.key === 'Escape') setEditing(false); }} />
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('atr.linkTitleHint')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontWeight: 'var(--fw-bold)' }} disabled={busy} onClick={guardar}>
                {busy ? '…' : share ? t('atr.rename') : selectable ? t('atr.generateAndAccept') : t('atr.generateLink')}
              </button>
              <button className="btn ghost sm" disabled={busy} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
              {err && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</span>}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontWeight: 'var(--fw-bold)' }} onClick={() => setEditing(true)}>
              <Link2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {selectable ? t('atr.generateAndAccept') : t('atr.generateLink')}
            </button>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('atr.noLinkYet')}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
      {cabecera}
      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)' }}>{share.title || video.fileName}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="input" readOnly value={share.url} onFocus={(e) => e.currentTarget.select()} style={{ flex: '1 1 220px', fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }} />
        <button className="btn sm" onClick={copy} title={t('atr.copy')}>{copied ? <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.copied')}</> : <><Copy size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.copy')}</>}</button>
        <a className="btn sm" href={share.url} target="_blank" rel="noopener noreferrer" title={t('atr.openLink')}><ExternalLink size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></a>
        <button className="btn ghost sm" onClick={() => setEditing(true)} title={t('atr.rename')}><PenLine size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.rename')}</button>
        <button className="btn ghost sm" disabled={busy} onClick={revocar} title={t('atr.revoke')} style={{ color: 'var(--red)' }}><Link2Off size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.revoke')}</button>
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {share.byName ? `${t('rec.by')} ${share.byName}` : ''}{share.createdAt ? ` · ${when(share.createdAt)}` : ''}
        {' · '}<Eye size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {share.views > 0 ? `${t('atr.views', { n: share.views })}${share.lastViewedAt ? ` · ${t('atr.lastView', { date: when(share.lastViewedAt) })}` : ''}` : t('atr.notOpened')}
      </div>
      {err && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</span>}
    </div>
  );
}

function AtrRow({ v, onSaved, claimId, selectable, accepted, onAccepted, suggestedTitle }: {
  v: Atr; onSaved: () => void; claimId: string; selectable?: boolean;
  accepted?: AtrAccepted | null; onAccepted?: () => void; suggestedTitle: string;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState(v.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [editingLink, setEditingLink] = useState(false);
  const [open, setOpen] = useState(false);
  const isAccepted = accepted?.videoRef === v.id;
  const video = v.kind === 'video' ? v.video ?? null : null;
  async function save() {
    if (!v.fileId) return;
    setBusy(true);
    try { await api.post(`/api/files/${v.fileId}/atr-notes`, { text: note }); setSaved(true); setTimeout(() => setSaved(false), 2000); onSaved(); }
    catch { /* */ } finally { setBusy(false); }
  }
  async function accept() {
    // Un archivo sin link: primero el link (el backend lo deja aceptado al crearlo).
    if (video && !video.share) { setOpen(true); setEditingLink(true); return; }
    setAccepting(true);
    try {
      await api.post(`/api/claims/${claimId}/atr-accept`, {
        videoRef: v.id, videoUrl: video?.share?.url ?? v.videoUrl, label: video?.share?.title ?? v.label ?? v.inspectorName,
      });
      onAccepted?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setAccepting(false); }
  }
  const infoArchivo = video ? [video.fileName, fmtBytes(video.sizeBytes), fmtDuration(video.durationMs), video.width && video.height ? `${video.width}×${video.height}` : ''].filter(Boolean).join(' · ') : null;
  return (
    <details open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} style={{ border: `1px solid ${isAccepted ? 'var(--green)' : 'var(--line)'}`, borderRadius: 10, background: isAccepted ? 'var(--green-soft)' : undefined }}>
      <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Video size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {v.label || v.inspectorName || t('cr.unassigned')}
        <span className="muted" style={{ fontWeight: 400 }}>· {day(v.at)}</span>
        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: v.status === 'uploaded' ? 'var(--green-soft)' : 'var(--orange-soft)', color: v.status === 'uploaded' ? 'var(--green)' : 'var(--orange)' }}>{v.status === 'uploaded' ? t('cr.up.uploaded') : t('cr.up.pending')}</span>
        {video && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)' }}>{t('atr.fileBadge')}</span>}
        {video?.codec === 'hevc' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--orange-soft)', color: 'var(--orange)' }} title={t('atr.hevcWarn')}>HEVC</span>}
        {video?.share && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)' }}><Link2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.linked')}</span>}
        <span style={{ flex: 1 }} />
        {v.notes && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)' }}><FilePenLine size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.hasNote')}</span>}
        {/* Aceptado: quién + fecha/hora. Si es seleccionable y aún no es el aceptado, botón Accept. */}
        {isAccepted ? (
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: 'var(--good-fill)', color: 'var(--good-fill-ink)' }}
            title={accepted?.at ? new Date(accepted.at).toLocaleString('en-US') : ''}>
            <Check size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.acceptedBy')} {accepted?.byName || '—'}{accepted?.at ? ` · ${when(accepted.at)}` : ''}
          </span>
        ) : selectable && v.status === 'uploaded' && v.videoUrl ? (
          <button className="btn sm" disabled={accepting} onClick={(e) => { e.preventDefault(); void accept(); }}
            style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-2xs)' }}>
            {accepting ? '…' : video && !video.share
              ? <><Link2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.generateAndAccept')}</>
              : <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.accept')}</>}
          </button>
        ) : null}
      </summary>
      <div style={{ padding: '0 10px 10px', display: 'grid', gap: 8 }}>
        {v.status === 'uploaded' && v.videoUrl
          ? <AtrVideoPlayer url={v.videoUrl} codec={video?.codec ?? null} />
          : <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Hourglass size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.awaitingVideo')}</div>}
        {infoArchivo && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{infoArchivo}{video?.uploadedBy ? ` · ${t('rec.by')} ${video.uploadedBy}` : ''}</div>}
        {v.resultNotes && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{v.resultNotes}</div>}

        {/* El link público del archivo (generar / copiar / renombrar / revocar). */}
        {video && <ShareBlock video={video} suggestedTitle={suggestedTitle} onChanged={onSaved} selectable={selectable} editing={editingLink} setEditing={setEditingLink} />}

        {/* Notas del ATR (para los que tienen fila en Files): editables, con quién + fecha; también se ven en Files. */}
        {v.fileId && (
          <div style={{ display: 'grid', gap: 4, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.3 }}><FilePenLine size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('atr.notes')}</span>
            <textarea className="input" style={{ width: '100%', minHeight: 48, fontSize: 'var(--fs-xs)' }} placeholder={t('atr.notesPh')} value={note} onChange={(e) => setNote(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} disabled={busy || note === (v.notes ?? '')} onClick={save}>{t('cr.saveNotes')}</button>
              {saved && <span style={{ color: 'var(--green)', fontSize: 'var(--fs-2xs)' }}><Check size={13} strokeWidth={1.75} /></span>}
              {v.notesBy && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rec.by')} {v.notesBy}{v.notesAt ? ` · ${new Date(v.notesAt).toLocaleString('en-US')}` : ''}</span>}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}

export function ClaimAtrBox({ claimId, selectable, onSelectionChange }: {
  claimId: string; selectable?: boolean; onSelectionChange?: (a: AtrAccepted | null) => void;
}) {
  const { t } = useI18n();
  const [videos, setVideos] = useState<Atr[]>([]);
  const [accepted, setAccepted] = useState<AtrAccepted | null>(null);
  const [suggestedTitle, setSuggestedTitle] = useState('ATR');
  const [loaded, setLoaded] = useState(false);
  const { fallo, marcar, limpiar } = useFallo();

  function load() {
    api.get<{ videos: Atr[]; accepted?: AtrAccepted | null; suggestedTitle?: string }>(`/api/claims/${claimId}/atr-videos`)
      .then((r) => { setVideos(r.videos); setAccepted(r.accepted ?? null); setSuggestedTitle(r.suggestedTitle || 'ATR'); onSelectionChange?.(r.accepted ?? null); })
      .then(() => limpiar())
      // Sin esto, un fallo dejaba videos en [] y el `return null` de abajo hacía DESAPARECER
      // la sección ATR del claim entera: ni el error ni el contenido, nada.
      .catch(marcar).finally(() => setLoaded(true));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);

  if (fallo) return <div id="sec-atr" className="card"><NoCargo onRetry={load} compact /></div>;
  if (loaded && videos.length === 0) return null;

  return (
    <div id="sec-atr" className="card">
      <h2 className="card-title"><Video size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> ATR <span className="badge gray">{videos.length}</span></h2>
      <div style={{ display: 'grid', gap: 8 }}>
        {videos.map((v) => <AtrRow key={v.id} v={v} onSaved={load} claimId={claimId} selectable={selectable} accepted={accepted} onAccepted={load} suggestedTitle={suggestedTitle} />)}
      </div>
      {selectable && !accepted && videos.some((v) => v.status === 'uploaded' && v.videoUrl) && (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 8 }}>{t('atr.acceptHint')}</div>
      )}
    </div>
  );
}
